/* eslint-disable max-nested-callbacks */
/* eslint-disable react/jsx-no-literals */
import React, { useEffect, useMemo, useRef } from 'react';
import { useQueries } from 'react-query';
import styled from 'styled-components';

import { getInitiativeChildren } from '@/features/canvas/api/initiative-relationships';
import { InitiativeFieldTypes } from '@/features/canvas/constants/initiative-field';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { components } from '@/types/api';

import { getInitiativeDates } from '../../utils/getInitiativeDates';
import { buildSectionTree, flattenTree } from '../../utils/treeUtils';

// Constants matching GanttTimelineSection measurements
const GANTT_BAR_HEIGHT = 16;
const GANTT_PADDING_ABOVE = 6;

// Variable heights for parent vs child rows
const GANTT_PARENT_HEIGHT = 28; // Height for parent rows
const GANTT_CHILD_HEIGHT = 20; // Height for child rows (more compact)

const GANTT_SECTION_PADDING = GANTT_PADDING_ABOVE;

// Helper function to get row height based on depth
const getRowHeight = (depth: number) => (depth > 0 ? GANTT_CHILD_HEIGHT : GANTT_PARENT_HEIGHT);

// Helper function to calculate cumulative position
const calculateCumulativePosition = (items: { depth: number }[], index: number) => {
  let position = GANTT_SECTION_PADDING;
  for (let i = 0; i < index; i += 1) {
    position += getRowHeight(items[i].depth);
  }
  return position;
};

// Progress column width
const PROGRESS_COLUMN_WIDTH = 70;

interface GanttProgressColumnProps {
  enhancedFilteredNodes: FilteredNodes[];
  expandedRows: Set<number>;
  getSectionHeight: (sectionId: number) => number;
  isDependencyMode: boolean;
  initiativesWithFields?: components['schemas']['Initiative'][];
}

// Helper function to calculate node filter state (matches CanvasNodes logic)
/* eslint-disable @typescript-eslint/no-explicit-any */
const calculateNodeFilterState = (
  node: FilteredNodes,
  filters: any[] | undefined,
  activeFilterIds: number[],
  optionCheckedIds: number[]
) => {
  const activeFilters =
    filters?.filter((f: any) => f.id !== undefined && activeFilterIds.includes(f.id!)) || [];

  const doesNodeHaveActiveFilter = activeFilters.some(
    (filter: any) => filter.nodes?.some((n: any) => n.nodeId === node.id) ?? false
  );

  const isNodesOptionChecked = activeFilters.every((filter: any) =>
    filter.selection === 'multiple'
      ? filter.nodes?.some(
          (n: any) => n.nodeId === node.id && optionCheckedIds.includes(n.optionId ?? -1)
        ) ?? false
      : optionCheckedIds.includes(
          filter.nodes?.find((n: any) => n.nodeId === node.id)?.optionId ?? -1
        )
  );

  const isNotInFilter = Boolean(
    (doesNodeHaveActiveFilter && optionCheckedIds.length > 0 && !isNodesOptionChecked) ||
      (!doesNodeHaveActiveFilter && activeFilterIds.length > 0)
  );

  const isGreyDueToFilter = activeFilterIds.length > 0 && isNotInFilter;
  return isGreyDueToFilter ? 0.15 : 1;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const GanttProgressColumn: React.FC<GanttProgressColumnProps> = ({
  enhancedFilteredNodes,
  initiativesWithFields,
  expandedRows,
  getSectionHeight,
  isDependencyMode,
}) => {
  const {
    sortedVerticalSections,
    sortedHorizontalSections,
    activeFilterIds,
    optionCheckedIds,
    filters,
    roadmapNodes,
    hiddenChildNodeIds,
  } = useEditorContext();
  const containerRef = useRef<HTMLDivElement>(null);

  // Combine sections exactly like other Gantt components
  const allSections = useMemo(
    () => [...sortedVerticalSections, ...sortedHorizontalSections],
    [sortedVerticalSections, sortedHorizontalSections]
  );

  // Use enhanced filtered nodes directly - no global re-adding of hidden children
  const allEnhancedNodes = enhancedFilteredNodes;

  // Get unique initiative IDs for fetching children relationships
  const initiativeIds = useMemo(() => {
    const ids = new Set<number>();
    roadmapNodes?.forEach(node => {
      if (node.initiatives?.id) {
        ids.add(node.initiatives.id);
      }
    });
    return Array.from(ids);
  }, [roadmapNodes]);

  // Fetch children for all initiatives
  const childrenQueries = useQueries(
    initiativeIds.map(id => ({
      queryKey: ['getInitiativeChildren', id],
      queryFn: () => getInitiativeChildren({ initiativeId: id }),
      enabled: true,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }))
  );

  // Check if queries are still loading
  const areQueriesLoading = childrenQueries.some(query => query.isLoading);

  // Create parent-child relationships map from query data
  const parentChildMap = useMemo(() => {
    // Don't build relationships until queries are done loading
    if (areQueriesLoading) {
      return new Map<number, components['schemas']['Initiative'][]>();
    }

    const map = new Map<number, components['schemas']['Initiative'][]>();
    childrenQueries.forEach((query, index) => {
      if (query.data && query.data.length > 0) {
        const parentId = initiativeIds[index]; // Use index to correlate with initiativeIds
        map.set(parentId, query.data);
      }
    });
    return map;
  }, [childrenQueries, initiativeIds, areQueriesLoading]);

  // Group initiatives by section using tree structure
  const sectionProgressData = useMemo(() => {
    const result: Record<
      number,
      { depth: number; id: number; opacity: number; progress: number }[]
    > = {};

    allSections.forEach(section => {
      if (section.id !== undefined) {
        // Start with nodes that are naturally in this section
        const baseSectionNodes = allEnhancedNodes.filter(
          node => node.sectionId === section.id && node.initiatives
        );

        // Add hidden children that belong to parents in this section (multi-level)
        const enhancedSectionNodes = [...baseSectionNodes];
        const sectionNodeIds = new Set(baseSectionNodes.map(n => n.id));

        // Keep adding children until no more are found (handles multi-level nesting)
        let nodesToProcess = [...baseSectionNodes];
        const processedIds = new Set(baseSectionNodes.map(n => n.initiatives?.id).filter(Boolean));

        while (nodesToProcess.length > 0) {
          const newlyAdded: FilteredNodes[] = [];

          nodesToProcess.forEach(parentNode => {
            if (parentNode.initiatives?.id) {
              const childrenOfParent = parentChildMap.get(parentNode.initiatives.id) || [];

              childrenOfParent.forEach(childInitiative => {
                if (
                  childInitiative.id &&
                  !processedIds.has(childInitiative.id) &&
                  !sectionNodeIds.has(childInitiative.id)
                ) {
                  const childNodeInRoadmap = roadmapNodes?.find(
                    n => n.initiatives?.id === childInitiative.id
                  );

                  if (
                    childNodeInRoadmap &&
                    hiddenChildNodeIds.has(childNodeInRoadmap.id!) &&
                    childNodeInRoadmap.isVisible !== false
                  ) {
                    // Find the corresponding initiative with fields if available
                    const initiativeWithFields = initiativesWithFields?.find(
                      initiative => initiative.id === childNodeInRoadmap.initiatives?.id
                    );

                    // Create enhanced child node
                    const enhancedChild = {
                      ...childNodeInRoadmap,
                      initiatives: initiativeWithFields || childNodeInRoadmap.initiatives,
                      color: '',
                      shape: 'circle' as const,
                      size: undefined,
                      isVisible: true,
                    };
                    enhancedSectionNodes.push(enhancedChild);
                    newlyAdded.push(enhancedChild);
                    sectionNodeIds.add(childNodeInRoadmap.id!);
                    processedIds.add(childInitiative.id);
                  }
                }
              });
            }
          });

          nodesToProcess = newlyAdded; // Process newly added nodes for THEIR children
        }

        // Build tree structure using enhanced section nodes
        const tree = buildSectionTree(enhancedSectionNodes, parentChildMap);

        // Flatten the tree based on expanded state
        const flattened = flattenTree(tree, expandedRows);

        // Create a map of child to parent for date inheritance
        const childToParentMap = new Map<number, FilteredNodes>();
        tree.forEach(root => {
          const mapParentChild = (parent: (typeof tree)[0]) => {
            if (parent.children) {
              parent.children.forEach(child => {
                if (child.node?.initiatives?.id && parent.node?.initiatives?.id) {
                  childToParentMap.set(child.node.initiatives.id, parent.node);
                }
                mapParentChild(child);
              });
            }
          };
          mapParentChild(root);
        });

        // Process each item from the flattened tree
        const progressData = flattened
          .filter(item => item.node.id !== undefined)
          .map(item => {
            let { node } = item;
            const { depth } = item;

            // Check if this node needs date inheritance
            if (node.initiatives?.id && childToParentMap.has(node.initiatives.id)) {
              const parent = childToParentMap.get(node.initiatives.id)!;
              const { startDate: childStartDate, endDate: childEndDate } = getInitiativeDates(
                node.initiatives
              );
              const { startDate: parentStartDate, endDate: parentEndDate } = getInitiativeDates(
                parent.initiatives
              );

              // Check if we need to inherit any dates
              const needsStartDate = !childStartDate && parentStartDate;
              const needsEndDate = !childEndDate && parentEndDate;

              if (needsStartDate || needsEndDate) {
                // Create a new node with inherited dates
                const updatedFields = [...(node.initiatives.fields || [])];

                // Helper function to update date fields
                const updateDateField = (fieldTitle: string, dateValue: string) => {
                  const fieldIndex = updatedFields.findIndex(f => f.title === fieldTitle);
                  if (fieldIndex >= 0) {
                    updatedFields[fieldIndex] = {
                      ...updatedFields[fieldIndex],
                      value: dateValue,
                    };
                  } else {
                    updatedFields.push({ type: 'date', title: fieldTitle, value: dateValue });
                  }
                };

                if (needsStartDate) {
                  updateDateField('Start Date', parentStartDate);
                }
                if (needsEndDate) {
                  updateDateField('End Date', parentEndDate);
                }

                node = {
                  ...node,
                  initiatives: {
                    ...node.initiatives,
                    fields: updatedFields,
                  },
                };
              }
            }
            // Extract progress using the same logic as Swimlane
            const progressValue =
              node.initiatives?.progressField ||
              node.initiatives?.fields?.find(f => f.type === InitiativeFieldTypes.progressBar)
                ?.value ||
              '0';

            const numericValue = Number(progressValue);
            const progress = Number.isNaN(numericValue)
              ? 0
              : Math.min(Math.max(numericValue, 0), 100);

            // Calculate opacity using helper function (matches CanvasNodes logic)
            let opacity = calculateNodeFilterState(
              node,
              filters,
              activeFilterIds,
              optionCheckedIds
            );

            // Also grey out orphaned initiatives ONLY when dependency mode is ON
            if (isDependencyMode) {
              const nodeId = node.id;
              const initiativeId = node.initiatives?.id;

              // Check outgoing dependencies
              const hasOutgoingDependencies = node.dependencies && node.dependencies.length > 0;

              // Check incoming dependencies - search in enhancedSectionNodes which includes all injected children
              const hasIncomingDependencies = enhancedSectionNodes.some(otherNode =>
                otherNode.dependencies?.some(
                  dep =>
                    dep.masterNodeId === nodeId ||
                    dep.masterNodeId === initiativeId ||
                    dep.slaveNodeId === nodeId ||
                    dep.slaveNodeId === initiativeId
                )
              );

              // An orphan has no dependencies in either direction
              const isOrphan = !hasOutgoingDependencies && !hasIncomingDependencies;
              if (isOrphan) {
                opacity = 0.15;
              }
            }

            return {
              id: node.id!,
              progress,
              opacity,
              depth,
            };
          });

        result[section.id] = progressData;
      }
    });

    return result;
  }, [
    allEnhancedNodes,
    allSections,
    activeFilterIds,
    optionCheckedIds,
    filters,
    isDependencyMode,
    parentChildMap,
    expandedRows,
    roadmapNodes,
    hiddenChildNodeIds,
    initiativesWithFields,
  ]);

  // Calculate total height and force container to be tall enough for scrolling
  useEffect(() => {
    if (containerRef.current) {
      const totalHeight = allSections.reduce((height, section) => {
        if (section.id === undefined) return height;
        const progressItems = sectionProgressData[section.id] || [];
        const calculatedHeight =
          progressItems.length > 0
            ? progressItems.reduce((sum, item) => sum + getRowHeight(item.depth), 0) +
              GANTT_SECTION_PADDING
            : 97.06;
        // Use the sidebar's calculated height, or fall back to our calculation
        const sidebarHeight = getSectionHeight(section.id);
        const sectionHeight = Math.max(calculatedHeight, sidebarHeight, 97.06);
        return height + sectionHeight;
      }, 0);

      // Ensure the container is tall enough to require scrolling
      containerRef.current.style.minHeight = `${Math.max(totalHeight, 800)}px`;
    }
  }, [allSections, sectionProgressData, getSectionHeight]);

  return (
    <ProgressColumnContainer ref={containerRef}>
      {allSections.map((section, sectionIndex) => {
        if (section.id === undefined) return null;

        const progressItems = sectionProgressData[section.id] || [];
        const isLastSection = sectionIndex === allSections.length - 1;

        const calculatedHeight =
          progressItems.length > 0
            ? progressItems.reduce((sum, item) => sum + getRowHeight(item.depth), 0) +
              GANTT_SECTION_PADDING
            : 97.06; // Use SWIMLANE_MIN_HEIGHT for consistency
        const sidebarHeight = getSectionHeight(section.id);
        const finalSectionHeight = Math.max(calculatedHeight, sidebarHeight, 97.06);

        return (
          <ProgressSection
            key={section.id}
            nodeCount={progressItems.length}
            sectionHeight={finalSectionHeight}
          >
            {progressItems.map(
              (
                item: { depth: number; id: number; opacity: number; progress: number },
                index: number
              ) => {
                const topPosition = calculateCumulativePosition(progressItems, index);
                const rowHeight = getRowHeight(item.depth);
                return (
                  <ProgressItem
                    key={item.id}
                    topPosition={topPosition}
                    rowHeight={rowHeight}
                    opacity={item.opacity}
                    depth={item.depth}
                  >
                    {item.progress}%
                  </ProgressItem>
                );
              }
            )}
            {/* Add horizontal lines matching GanttTimelineSection */}
            {progressItems.length > 0 && (
              <>
                {/* Line above first bar */}
                <HorizontalLine key="line-top" top={0} />
                {/* Lines between bars - positioned at equal 28px intervals, but skip between parent-child and child-child */}
                {Array.from({ length: Math.max(0, progressItems.length - 1) }, (_, index) => {
                  const currentDepth = progressItems[index]?.depth || 0;
                  const nextDepth = progressItems[index + 1]?.depth || 0;

                  // Skip line if current or next item is a child (depth > 0)
                  if (currentDepth > 0 || nextDepth > 0) {
                    return null;
                  }

                  const linePosition = calculateCumulativePosition(progressItems, index + 1);
                  return <HorizontalLine key={`line-${index}`} top={linePosition} />;
                })}
                {/* Line underneath last initiative when section doesn't fill minimum height */}
                {progressItems.length > 0 && calculatedHeight < finalSectionHeight && (
                  <HorizontalLine
                    key="line-after-last"
                    top={calculateCumulativePosition(progressItems, progressItems.length)}
                  />
                )}
                {/* Bottom line for the last section */}
                {isLastSection && (
                  <HorizontalLine
                    key="line-bottom"
                    top={calculateCumulativePosition(progressItems, progressItems.length)}
                  />
                )}
                {/* Bottom line for sections that don't fill minimum height (except last section) */}
                {!isLastSection && calculatedHeight < finalSectionHeight && (
                  <HorizontalLine key="line-section-bottom" top={finalSectionHeight} />
                )}
              </>
            )}
            {/* Add bottom line for empty sections (except last section) */}
            {progressItems.length === 0 && !isLastSection && (
              <HorizontalLine key="empty-section-line" top={finalSectionHeight} />
            )}
          </ProgressSection>
        );
      })}
    </ProgressColumnContainer>
  );
};

const ProgressColumnContainer = styled.div`
  width: ${PROGRESS_COLUMN_WIDTH}px;
  min-height: 100%;
  background: ${({ theme }) => theme.palette.brand.white};
  border-right: 1px solid #afafaf;
`;

const ProgressSection = styled.div<{ nodeCount: number; sectionHeight: number }>`
  position: relative;
  min-height: ${({ sectionHeight }) => sectionHeight}px;
  background: ${({ theme }) => theme.palette.brand.backgroundSidebar};
`;

const ProgressItem = styled.div<{
  depth: number;
  opacity: number;
  rowHeight: number;
  topPosition: number;
}>`
  position: absolute;
  top: ${({ topPosition, rowHeight }) =>
    topPosition + (rowHeight - GANTT_BAR_HEIGHT) / 2}px; // Center in row
  left: 0; // Always centered in progress column, not affected by depth indentation
  right: 0;
  height: ${GANTT_BAR_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: bold;
  color: ${({ theme }) => theme.palette.brand.primary};
  background: transparent;
  opacity: ${({ opacity }) => opacity};
`;

const HorizontalLine = styled.div<{ top: number }>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #dfdfdf;
  z-index: 1;
`;

export { GanttProgressColumn, PROGRESS_COLUMN_WIDTH };
